import { gql } from '@apollo/client';

export const GET_COMPANY_OVERVIEW = gql`
  query GetCompanyOverview($deal_opportunity_id: ID!) {
    companyOverviewByDeal(dealOpportunityId: $deal_opportunity_id) {
      id
      name
      businessDescription
      isCapexAssetLight
      isCapexIntensive
      totalCapexSpend
      maintenanceCapexSpend
      growthCapexSpend
      capexReferencePoint
      capexPercentageOfRevenue
      maintenanceCapexPercentage
      growthCapexPercentage
      capexCommentary
      customerAverageTenure
      customerRetentionPercentage
      customerTypeSelection
      recurringRevenuePercentage
      reOccurringRevenuePercentage
      projectBasedRevenuePercentage
      concentrationTop1
      concentrationTop3
      concentrationTop5
      concentrationTop10
      concentrationCommentary
      contractCommentary
      supplierCommentary
      keySupplierCommentary
      domesticInternational
      redundancyAlternateSources
      commodityExposure
      pricingTrends
      customerCommentary
      competitiveEdge
      companyProductsIds {
        id
        name
        description
        percentOfRevenue
      }
      companyServicesIds {
        id
        name
        description
        percentOfRevenue
      }
      customerEndMarketIds {
        id
        name
        description
      }
    }
  }
`;

export const GET_ALL_CUSTOMER_END_MARKETS = gql`
  query GetAllCustomerEndMarkets {
    customerEndMarkets {
      id
      name
      description
    }
  }
`;

export const GET_OPERATIONAL_INFRASTRUCTURE = gql`
  query GetOperationalInfrastructure($dealOpportunityId: ID!) {
    operationalInfrastructureByDeal(dealOpportunityId: $dealOpportunityId) {
      id
      technologyStack
      security
      scalability
      development
      support
    }
  }
`;

export const CREATE_OPERATIONAL_INFRASTRUCTURE = gql`
  mutation CreateOperationalInfrastructure(
    $values: CompanyOperationalInfrastructureInput!
  ) {
    createOperationalInfrastructure(values: $values) {
      ok
      message
      operationalInfrastructure {
        id
        technologyStack
        security
        scalability
        development
        support
      }
    }
  }
`;

export const UPDATE_OPERATIONAL_INFRASTRUCTURE = gql`
  mutation UpdateOperationalInfrastructure(
    $id: ID!
    $values: CompanyOperationalInfrastructureInput!
  ) {
    updateOperationalInfrastructure(id: $id, values: $values) {
      ok
      message
      operationalInfrastructure {
        id
        technologyStack
        security
        scalability
        development
        support
      }
    }
  }
`;

export const UPSERT_OPERATIONAL_INFRASTRUCTURE = gql`
  mutation UpsertOperationalInfrastructure(
    $dealOpportunityId: ID!
    $values: CompanyOperationalInfrastructureInput!
  ) {
    upsertOperationalInfrastructure(
      dealOpportunityId: $dealOpportunityId
      values: $values
    ) {
      ok
      message
      operationalInfrastructure {
        id
        technologyStack
        security
        scalability
        development
        support
      }
    }
  }
`;

export const GET_GMMR_RECORDS_BY_ACCOUNT = gql`
  query GetGmmrRecordsByAccount($id: Int!) {
    gmmrRecordsByAccount(accountId: $id) {
      edges {
        node {
          id
          name
          date
          totalGmmrScore
          boardGovernanceSectionScore
          accountingReportingSectionScore
          dataKpisSectionScore
          managementCompetenciesSectionScore
          strategicRoadmapSectionScore
          loyaltyTeamCultureEsgSectionScore
          technologyCybersecuritySectionScore
          gmmrSummary
          account {
            id
            name
          }
          accountId
        }
      }
    }
  }
`;

export const CREATE_GMMR = gql`
  mutation CreateGmmr($accountId: Int!, $values: GmmrInput!) {
    createGmmr(accountId: $accountId, values: $values) {
      ok
      gmmr {
        id
        name
        totalGmmrScore
      }
    }
  }
`;

export const UPDATE_GMMR = gql`
  mutation UpdateGmmr($id: ID!, $values: GmmrInput!) {
    updateGmmr(id: $id, values: $values) {
      ok
      gmmr {
        id
        name
        totalGmmrScore
      }
    }
  }
`;

export const UPDATE_COMPANY_OVERVIEW = gql`
  mutation UpdateCompanyOverview($id: ID!, $values: CompanyOverviewInput!) {
    updateCompanyOverview(id: $id, values: $values) {
      ok
      errors
      companyOverview {
        id
      }
    }
  }
`;

export const CREATE_COMPANY_PRODUCT = gql`
  mutation CreateCompanyProduct(
    $values: CompanyProductInput!
    $companyOverviewId: ID!
  ) {
    createCompanyProduct(
      values: $values
      companyOverviewId: $companyOverviewId
    ) {
      ok
      errors
      companyProduct {
        id
        name
        description
        percentOfRevenue
      }
    }
  }
`;

export const UPDATE_COMPANY_PRODUCT = gql`
  mutation UpdateCompanyProduct($id: ID!, $values: CompanyProductInput!) {
    updateCompanyProduct(id: $id, values: $values) {
      ok
      errors
      companyProduct {
        id
        name
        description
        percentOfRevenue
      }
    }
  }
`;

export const DELETE_COMPANY_PRODUCT = gql`
  mutation DeleteCompanyProduct($id: ID!) {
    deleteCompanyProduct(id: $id) {
      ok
      errors
    }
  }
`;

export const CREATE_COMPANY_SERVICE = gql`
  mutation CreateCompanyService(
    $values: CompanyServiceInput!
    $companyOverviewId: ID!
  ) {
    createCompanyService(
      values: $values
      companyOverviewId: $companyOverviewId
    ) {
      ok
      errors
      companyService {
        id
        name
        description
        percentOfRevenue
      }
    }
  }
`;

export const UPDATE_COMPANY_SERVICE = gql`
  mutation UpdateCompanyService($id: ID!, $values: CompanyServiceInput!) {
    updateCompanyService(id: $id, values: $values) {
      ok
      errors
      companyService {
        id
        name
        description
        percentOfRevenue
      }
    }
  }
`;

export const DELETE_COMPANY_SERVICE = gql`
  mutation DeleteCompanyService($id: ID!) {
    deleteCompanyService(id: $id) {
      ok
      errors
    }
  }
`;

export interface CompanyOverview {
  businessDescription: null | string;
  capexCommentary: null | string;
  capexPercentageOfRevenue: null | number;
  capexReferencePoint: null | string;
  commodityExposure: null | string;
  companyProductsIds: CompanyProduct[];
  companyServicesIds: CompanyService[];
  competitiveEdge: null | string;
  concentrationCommentary: null | string;
  concentrationTop1: null | number;
  concentrationTop10: null | number;
  concentrationTop3: null | number;
  concentrationTop5: null | number;
  contractCommentary: null | string;
  customerAverageTenure: null | number;
  customerCommentary: null | string;
  customerEndMarketIds: CustomerEndMarket[];
  customerRetentionPercentage: null | number;
  customerTypeSelection: null | string;
  domesticInternational: null | string;
  growthCapexPercentage: null | number;
  growthCapexSpend: null | number;
  id: string;
  isCapexAssetLight: boolean | null;
  isCapexIntensive: boolean | null;
  keySupplierCommentary: null | string;
  maintenanceCapexPercentage: null | number;
  maintenanceCapexSpend: null | number;
  name: null | string;
  pricingTrends: null | string;
  projectBasedRevenuePercentage: null | number;
  recurringRevenuePercentage: null | number;
  redundancyAlternateSources: null | string;
  reOccurringRevenuePercentage: null | number;
  supplierCommentary: null | string;
  totalCapexSpend: null | number;
}

export interface CompanyOverviewInput {
  businessDescription?: string;
  capexCommentary?: string;
  capexPercentageOfRevenue?: number;
  capexReferencePoint?: string;
  commodityExposure?: string;
  competitiveEdge?: string;
  concentrationCommentary?: string;
  concentrationTop1?: number;
  concentrationTop10?: number;
  concentrationTop3?: number;
  concentrationTop5?: number;
  contractCommentary?: string;
  customerAverageTenure?: number;
  customerCommentary?: string;
  customerEndMarketIds?: string[];
  customerRetentionPercentage?: number;
  customerTypeSelection?: string;
  domesticInternational?: string;
  growthCapexPercentage?: number;
  growthCapexSpend?: number;
  isCapexAssetLight?: boolean;
  isCapexIntensive?: boolean;
  keySupplierCommentary?: string;
  maintenanceCapexPercentage?: number;
  maintenanceCapexSpend?: number;
  name?: string;
  pricingTrends?: string;
  projectBasedRevenuePercentage?: number;
  recurringRevenuePercentage?: number;
  redundancyAlternateSources?: string;
  reOccurringRevenuePercentage?: number;
  supplierCommentary?: string;
  totalCapexSpend?: number;
}

export interface CompanyProduct {
  description: null | string;
  id: string;
  name: null | string;
  percentOfRevenue: null | number;
}

export interface CompanyProductInput {
  description?: string;
  name?: string;
  percentOfRevenue?: number;
}

export interface CompanyService {
  description: null | string;
  id: string;
  name: null | string;
  percentOfRevenue: null | number;
}

export interface CompanyServiceInput {
  description?: string;
  name?: string;
  percentOfRevenue?: number;
}

export interface CreateCompanyProductResponse {
  createCompanyProduct: {
    companyProduct: CompanyProduct | null;
    errors: string[];
    ok: boolean;
  };
}

export interface CreateCompanyServiceResponse {
  createCompanyService: {
    companyService: CompanyService | null;
    errors: string[];
    ok: boolean;
  };
}

export interface CreateGmmrResponse {
  createGmmr: {
    gmmr: {
      id: string;
      name: null | string;
      totalGmmrScore: null | number;
    } | null;
    ok: boolean;
  };
}

export interface UpdateGmmrResponse {
  updateGmmr: {
    gmmr: {
      id: string;
      name: null | string;
      totalGmmrScore: null | number;
    } | null;
    ok: boolean;
  };
}

export interface CreateOperationalInfrastructureResponse {
  createOperationalInfrastructure: {
    message: null | string;
    ok: boolean;
    operationalInfrastructure: null | OperationalInfrastructure;
  };
}

export interface CustomerEndMarket {
  description: null | string;
  id: string;
  name: 'b2b' | 'b2c' | 'government';
}

export interface DeleteCompanyProductResponse {
  deleteCompanyProduct: {
    errors: string[];
    ok: boolean;
  };
}

export interface DeleteCompanyServiceResponse {
  deleteCompanyService: {
    errors: string[];
    ok: boolean;
  };
}

export interface GetAllCustomerEndMarketsResponse {
  customerEndMarkets: CustomerEndMarket[];
}

export interface GetCompanyOverviewResponse {
  companyOverviewByDeal: CompanyOverview | null;
}

export interface GetGmmrRecordsByAccountResponse {
  gmmrRecordsByAccount: {
    edges: {
      node: GmmrRecordNode;
    }[];
  } | null;
}

export interface GetOperationalInfrastructureResponse {
  operationalInfrastructureByDeal: null | OperationalInfrastructure;
}

export interface GmmrRecordNode {
  account: {
    id: string;
    name: null | string;
  } | null;
  accountId: null | number;
  accountingReportingSectionScore: null | number;
  boardGovernanceSectionScore: null | number;
  dataKpisSectionScore: null | number;
  date: null | string;
  gmmrSummary: null | Record<string, unknown> | string;
  id: string;
  loyaltyTeamCultureEsgSectionScore: null | number;
  managementCompetenciesSectionScore: null | number;
  name: null | string;
  strategicRoadmapSectionScore: null | number;
  technologyCybersecuritySectionScore: null | number;
  totalGmmrScore: null | number;
}

export interface OperationalInfrastructure {
  development: null | string;
  id: string;
  scalability: null | string;
  security: null | string;
  support: null | string;
  technologyStack: null | string;
}

export interface OperationalInfrastructureInput {
  dealOpportunityId?: string;
  development?: string;
  scalability?: string;
  security?: string;
  support?: string;
  technologyStack?: string;
}

export interface UpdateCompanyOverviewResponse {
  updateCompanyOverview: {
    companyOverview: { id: string } | null;
    errors: string[];
    ok: boolean;
  };
}

export interface UpdateCompanyProductResponse {
  updateCompanyProduct: {
    companyProduct: CompanyProduct | null;
    errors: string[];
    ok: boolean;
  };
}

export interface UpdateCompanyServiceResponse {
  updateCompanyService: {
    companyService: CompanyService | null;
    errors: string[];
    ok: boolean;
  };
}

export interface UpdateOperationalInfrastructureResponse {
  updateOperationalInfrastructure: {
    message: null | string;
    ok: boolean;
    operationalInfrastructure: null | OperationalInfrastructure;
  };
}

export interface UpsertOperationalInfrastructureResponse {
  upsertOperationalInfrastructure: {
    message: null | string;
    ok: boolean;
    operationalInfrastructure: null | OperationalInfrastructure;
  };
}
